import { E as EmblaCarousel } from "./embla-carousel.esm-3bCdUmQG.js";
import { d as debounce, i as isMobile, l as lazyInit } from "./utilities-2Suih3Nm.js";
import { a as addPrevNextBtnsClickHandlersSSR, b as addDotsBtnsAndClickHandlersSSR, c as addInstagramDotBtnsAndClickHandlers } from "./embla-extension-OubijMX1.js";
const MARQUEE_EASE = 0.06;
const MARQUEE_SETTLE = 0.5;
const MARQUEE_MAX_CLONES = 30;
class ListingComponent extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this._marqueeActive = false;
    this._marqueeRafId = null;
    this._marqueeLastTime = 0;
    this._marqueePos = 0;
    this._marqueeVel = 0;
    this._marqueeTargetVel = 0;
    this._marqueeBaseSpeed = 0;
    this._marqueeDir = -1;
    this._marqueeGroupW = 0;
    this._marqueeCssIdle = false;
    this._marqueeAnimPending = false;
    this._marqueeDragging = false;
    this._marqueeDragStartX = 0;
    this._marqueeDragStartPos = 0;
    this._marqueeDragLastX = 0;
    this._marqueeDragLastT = 0;
    this._marqueeDragVel = 0;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtn: null,
      nextBtn: null,
      dots: null
    };
    this.config = {
      showNavigation: false,
      showDots: false,
      options: {},
      autoScroll: {
        desktop: false,
        mobile: false
      }
    };
    this.gridConfig = {
      desktop: { rows: 1, itemsPerRow: 4 },
      mobile: { rows: 1, itemsPerRow: 1 }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
    this._reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this._onMotionChange = () => this.init();
  }
  connectedCallback() {
    this.cacheNodes();
    this.ssrRows = Number(this.dataset.ssrRows) || 1;
    this._domRows = this.ssrRows;
    this.gridConfig = {
      desktop: {
        rows: Number(this.dataset.rowsDesktop) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowDesktop) || 4
      },
      mobile: {
        rows: Number(this.dataset.rowsMobile) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowMobile) || 1
      }
    };
    const container = this.nodes.container;
    if (container) {
      this.originalItems = Array.from(
        container.querySelectorAll(":scope > .embla__slide > *")
      );
    } else {
      this.originalItems = [];
    }
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    if (Shopify.designMode) {
      this.init();
    } else {
      lazyInit(this, () => this.init());
    }
    window.addEventListener("resize", this.handleResize);
    this._reducedMotionQuery.addEventListener("change", this._onMotionChange);
  }
  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this._reducedMotionQuery.removeEventListener("change", this._onMotionChange);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) return;
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    this.nodes.prevBtn = this.nodes.embla.querySelector(".embla__snip-button--prev");
    this.nodes.nextBtn = this.nodes.embla.querySelector(".embla__snip-button--next");
    this.nodes.dots = this.nodes.embla.querySelector(".embla__snip-dots");
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showDots = this.dataset.showDots === "true";
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoScrollDesktop = this.parseJSON(this.dataset.autoScrollDesktop, false);
    const autoScrollMobile = this.parseJSON(this.dataset.autoScrollMobile, false);
    this.config.autoScroll.desktop = autoScrollDesktop || false;
    this.config.autoScroll.mobile = autoScrollMobile || autoScrollDesktop;
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) return fallback;
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      if (typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
      return fallback;
    } catch (error) {
      return fallback;
    }
  }
  getCurrentAutoScrollConfig() {
    return isMobile() ? this.config.autoScroll.mobile : this.config.autoScroll.desktop;
  }
  getCurrentGridConfig() {
    return isMobile() ? this.gridConfig.mobile : this.gridConfig.desktop;
  }
  // ========== Initialization (Router) ==========
  init() {
    this.destroy();
    const currentRows = this.getCurrentGridConfig().rows;
    if (this._domRows !== currentRows) {
      currentRows > 1 ? this.applyGridLayout() : this.flattenLayout();
      this._domRows = currentRows;
    }
    const autoScrollConfig = this.getCurrentAutoScrollConfig();
    if (autoScrollConfig && !this._reducedMotionQuery.matches) {
      this.initMarquee(autoScrollConfig);
    } else {
      this.initEmbla();
    }
  }
  // ========== Embla Mode ==========
  initEmbla() {
    if (!this.nodes.viewport) return;
    try {
      this.emblaApi = EmblaCarousel(
        this.nodes.viewport,
        this.config.options,
        []
        // No plugins — auto-scroll handled by marquee
      );
      this.setupEmblaFeatures();
      this._isFirstInit = false;
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    const isActive = this.emblaApi.internalEngine().options.active;
    const snapCount = this.emblaApi.scrollSnapList().length;
    if (this.nodes.dots) this.nodes.dots.style.display = "";
    if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "";
    if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "";
    if (!isActive || snapCount <= 1) {
      if (this.nodes.dots) this.nodes.dots.style.display = "none";
      if (this.nodes.prevBtn) this.nodes.prevBtn.style.display = "none";
      if (this.nodes.nextBtn) this.nodes.nextBtn.style.display = "none";
      return;
    }
    if (this.config.showNavigation) this.enableNavigation();
    if (this.config.showDots) this.enableDots();
  }
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableDots() {
    const { dots } = this.nodes;
    if (!dots || !this.emblaApi) return;
    const dotsInner = dots.querySelector(".embla__dots-inner") || dots;
    const ssrDotCount = dotsInner.querySelectorAll(".embla__dot").length;
    const snapCount = this.emblaApi.scrollSnapList().length;
    let cleanup;
    if (this._isFirstInit && ssrDotCount === snapCount) {
      cleanup = addDotsBtnsAndClickHandlersSSR(this.emblaApi, dots);
    } else {
      cleanup = addInstagramDotBtnsAndClickHandlers(this.emblaApi, dotsInner);
    }
    this.registerCleanup(cleanup);
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    if (this.emblaApi) {
      this.emblaApi.scrollTo(slideIndex);
    } else if (this._marqueeActive) {
      const container = this.nodes.container;
      if (!container) return;
      const slides = Array.from(container.querySelectorAll(":scope > .embla__slide:not([data-cloned])"));
      if (slideIndex < 0 || slideIndex >= slides.length) return;
      this.pauseMarquee();
      const containerStyle = getComputedStyle(container);
      const gap = parseFloat(containerStyle.columnGap) || 0;
      let targetPos = 0;
      for (let i = 0; i < slideIndex; i++) {
        targetPos += slides[i].getBoundingClientRect().width + gap;
      }
      this._marqueePos = -targetPos;
      container.style.animation = "none";
      container.style.transform = `translateX(${this._marqueePos}px)`;
    }
  }
  pauseMarquee() {
    if (!this._marqueeActive) return;
    this._marqueeEnterRAFMode();
    this._marqueeTargetVel = 0;
  }
  resumeMarquee() {
    if (!this._marqueeActive || this._reducedMotionQuery.matches) return;
    this._marqueeTargetVel = this._marqueeBaseSpeed * this._marqueeDir;
    if (!this._marqueeCssIdle) this._marqueeStartRAF();
  }
  // ========== Marquee Mode ==========
  initMarquee(config) {
    var _a;
    this._marqueeActive = true;
    this._marqueeBaseSpeed = (config.speed || 1) * 60;
    this._marqueeDir = config.direction === "backward" ? 1 : -1;
    const hoverPause = config.stopOnMouseEnter === true;
    const bp = this.config.options.breakpoints || {};
    const bpKey = isMobile() ? "(max-width: 991px)" : "(min-width: 992px)";
    const dragEnabled = ((_a = bp[bpKey]) == null ? void 0 : _a.watchDrag) !== false;
    this._marqueeMeasure();
    if (this._marqueeGroupW <= 0) return;
    this._marqueeVel = this._marqueeTargetVel = this._marqueeBaseSpeed * this._marqueeDir;
    this._marqueePos = 0;
    this._marqueeIO = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          if (this._marqueeCssIdle) {
            this._marqueeSyncCSS();
            this.nodes.container.style.animationPlayState = "running";
          } else {
            this._marqueeStartRAF();
          }
        } else {
          this._marqueeStopRAF();
          if (this._marqueeCssIdle && this.nodes.container) {
            this.nodes.container.style.animationPlayState = "paused";
          }
        }
      }
    }, { rootMargin: "100px" });
    this._marqueeIO.observe(this);
    if (hoverPause) {
      this._marqueeEnter = () => {
        if (this._reducedMotionQuery.matches) return;
        if (this._marqueeCssIdle && this.nodes.container) {
          this.nodes.container.style.animationPlayState = "paused";
        } else {
          this._marqueeTargetVel = 0;
        }
      };
      this._marqueeLeave = () => {
        if (this._reducedMotionQuery.matches || this._marqueeDragging) return;
        if (this._marqueeCssIdle && this.nodes.container) {
          this.nodes.container.style.animationPlayState = "running";
        } else {
          this._marqueeTargetVel = this._marqueeBaseSpeed * this._marqueeDir;
          this._marqueeStartRAF();
        }
      };
      this.nodes.viewport.addEventListener("pointerenter", this._marqueeEnter);
      this.nodes.viewport.addEventListener("pointerleave", this._marqueeLeave);
    }
    if (dragEnabled) {
      this._marqueeOnDown = (e) => {
        if (e.button !== 0) return;
        this._marqueeEnterRAFMode();
        this._marqueeDragging = true;
        this._marqueeDragStartX = this._marqueeDragLastX = e.clientX;
        this._marqueeDragStartPos = this._marqueePos;
        this._marqueeDragLastT = performance.now();
        this._marqueeDragVel = 0;
        this._marqueeVel = this._marqueeTargetVel = 0;
        this.nodes.viewport.setPointerCapture(e.pointerId);
        this.nodes.viewport.style.cursor = "grabbing";
      };
      this._marqueeOnMove = (e) => {
        if (!this._marqueeDragging) return;
        e.preventDefault();
        const x = e.clientX;
        const t = performance.now();
        const dtMs = t - this._marqueeDragLastT;
        if (dtMs > 0) {
          this._marqueeDragVel = this._marqueeDragVel * 0.5 + (x - this._marqueeDragLastX) / dtMs * 500;
        }
        this._marqueePos = this._marqueeDragStartPos + (x - this._marqueeDragStartX);
        this._marqueeDragLastX = x;
        this._marqueeDragLastT = t;
      };
      this._marqueeOnUp = (e) => {
        if (!this._marqueeDragging) return;
        this._marqueeDragging = false;
        this.nodes.viewport.releasePointerCapture(e.pointerId);
        this.nodes.viewport.style.cursor = "grab";
        const cap = this._marqueeBaseSpeed * 6;
        this._marqueeVel = Math.max(-cap, Math.min(cap, this._marqueeDragVel));
        if (!this._reducedMotionQuery.matches) {
          this._marqueeTargetVel = this._marqueeBaseSpeed * this._marqueeDir;
        }
      };
      this.nodes.viewport.addEventListener("pointerdown", this._marqueeOnDown);
      this.nodes.viewport.addEventListener("pointermove", this._marqueeOnMove);
      this.nodes.viewport.addEventListener("pointerup", this._marqueeOnUp);
      this.nodes.viewport.style.touchAction = "pan-y";
      this.nodes.viewport.style.cursor = "grab";
    }
    this._marqueeRO = new ResizeObserver(() => {
      const wasCSSIdle = this._marqueeCssIdle;
      if (wasCSSIdle) this._marqueeEnterRAFMode();
      this._marqueeMeasure();
      if (wasCSSIdle && this._marqueeGroupW > 0) {
        this._marqueePos = 0;
        this._marqueeEnterCSSMode();
      }
    });
    this._marqueeRO.observe(this.nodes.viewport);
    this._marqueeEnterCSSMode();
  }
  // ── Measure & Clone ──
  _marqueeMeasure() {
    const container = this.nodes.container;
    if (!container) return;
    container.querySelectorAll("[data-cloned]").forEach((el) => el.remove());
    const slides = Array.from(container.querySelectorAll(":scope > .embla__slide:not([data-cloned])"));
    if (slides.length < 1) return;
    const containerStyle = getComputedStyle(container);
    const gap = parseFloat(containerStyle.columnGap) || 0;
    const slideWidths = slides.map((s) => s.getBoundingClientRect().width);
    const viewportW = this.nodes.viewport.getBoundingClientRect().width;
    let groupW = 0;
    for (const w of slideWidths) groupW += w + gap;
    this._marqueeGroupW = groupW;
    if (groupW <= 0) return;
    const need = viewportW + groupW;
    let currentW = groupW;
    const cloneList = [];
    while (currentW < need && cloneList.length < slides.length * MARQUEE_MAX_CLONES) {
      for (let i = 0; i < slides.length; i++) {
        if (currentW >= need) break;
        cloneList.push(i);
        currentW += slideWidths[i] + gap;
      }
    }
    if (cloneList.length > 0) {
      const frag = document.createDocumentFragment();
      for (const idx of cloneList) {
        const clone = slides[idx].cloneNode(true);
        clone.querySelectorAll("img").forEach((img) => {
          img.setAttribute("loaded", "");
          if (img.parentElement) img.parentElement.removeAttribute("data-lazy-loading");
        });
        clone.dataset.cloned = "";
        clone.setAttribute("aria-hidden", "true");
        frag.appendChild(clone);
      }
      container.appendChild(frag);
    }
  }
  // ── CSS Idle Mode ──
  _marqueeEnterCSSMode() {
    if (this._marqueeCssIdle || this._marqueeGroupW <= 0) return;
    this._marqueeCssIdle = true;
    this._marqueeStopRAF();
    this._marqueeSyncCSS();
  }
  _marqueeSyncCSS() {
    const container = this.nodes.container;
    if (!this._marqueeCssIdle || !container || this._marqueeGroupW <= 0) return;
    const speed = this._marqueeBaseSpeed;
    if (speed <= 0) {
      container.style.animation = "none";
      return;
    }
    const w = this._marqueeGroupW;
    const duration = w / speed;
    const direction = this._marqueeDir === -1 ? "normal" : "reverse";
    container.style.setProperty("--_marquee-gw", w);
    const normalizedPos = (this._marqueePos % w + w) % w;
    const fraction = this._marqueeDir === -1 ? (w - normalizedPos) / w : normalizedPos / w;
    const delay = -(fraction * duration);
    container.style.animation = `embla-marquee-slide ${duration}s linear ${delay}s infinite ${direction}`;
    container.style.animationPlayState = "running";
  }
  _marqueeEnterRAFMode() {
    if (!this._marqueeCssIdle) return;
    const container = this.nodes.container;
    if (container) {
      container.style.animationPlayState = "paused";
      const mat = new DOMMatrix(getComputedStyle(container).transform);
      this._marqueePos = mat.m41;
    }
    this._marqueeCssIdle = false;
    this._marqueeAnimPending = true;
    this._marqueeStartRAF();
  }
  // ── rAF Loop ──
  _marqueeStartRAF() {
    if (this._marqueeRafId !== null) return;
    this._marqueeLastTime = performance.now();
    this._marqueeRafId = requestAnimationFrame((ts) => this._marqueeTick(ts));
  }
  _marqueeStopRAF() {
    if (this._marqueeRafId !== null) {
      cancelAnimationFrame(this._marqueeRafId);
      this._marqueeRafId = null;
    }
  }
  _marqueeTick(ts) {
    this._marqueeRafId = null;
    if (!this._marqueeActive || this._marqueeCssIdle) return;
    const dt = Math.min((ts - this._marqueeLastTime) / 1e3, 0.06);
    this._marqueeLastTime = ts;
    const container = this.nodes.container;
    if (!container || this._marqueeGroupW <= 0) return;
    if (this._marqueeAnimPending) {
      this._marqueeAnimPending = false;
      container.style.animation = "none";
      container.style.transform = `translateX(${this._marqueePos}px)`;
      this._marqueeRafId = requestAnimationFrame((t) => this._marqueeTick(t));
      return;
    }
    if (!this._marqueeDragging) {
      const d = this._marqueeTargetVel - this._marqueeVel;
      if (Math.abs(d) < MARQUEE_SETTLE) {
        this._marqueeVel = this._marqueeTargetVel;
      } else {
        this._marqueeVel += d * MARQUEE_EASE;
      }
      this._marqueePos += this._marqueeVel * dt;
    }
    const w = this._marqueeGroupW;
    this._marqueePos = (this._marqueePos % w + w) % w;
    if (this._marqueePos > 0) this._marqueePos -= w;
    container.style.transform = `translateX(${this._marqueePos}px)`;
    if (!this._marqueeDragging && this._marqueeVel === this._marqueeTargetVel) {
      if (this._marqueeTargetVel !== 0) {
        this._marqueeEnterCSSMode();
      }
      return;
    }
    this._marqueeRafId = requestAnimationFrame((t) => this._marqueeTick(t));
  }
  // ========== Resize Handling ==========
  onResize() {
    const newDeviceType = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDeviceType) {
      this.currentDeviceType = newDeviceType;
      this.init();
    }
  }
  // ========== Grid Layout ==========
  flattenLayout() {
    const container = this.nodes.container;
    if (!container) return;
    const slides = this.originalItems.map((item) => {
      const slide = document.createElement("div");
      slide.className = "embla__slide";
      slide.appendChild(item);
      return slide;
    });
    container.replaceChildren(...slides);
  }
  applyGridLayout() {
    const { rows, itemsPerRow } = this.getCurrentGridConfig();
    const itemsPerPage = rows * itemsPerRow;
    const items = this.originalItems;
    const slides = [];
    for (let pageStart = 0; pageStart < items.length; pageStart += itemsPerPage) {
      const page = items.slice(pageStart, pageStart + itemsPerPage);
      const isPartial = page.length < itemsPerPage;
      if (isPartial) {
        for (let i = 0; i < page.length; i += rows) {
          const slide = document.createElement("div");
          slide.className = "embla__slide embla__slide-group";
          slide.style.setProperty("--slide-rows", rows);
          for (let r = 0; r < rows && i + r < page.length; r++) {
            slide.appendChild(page[i + r]);
          }
          slides.push(slide);
        }
      } else {
        for (let col = 0; col < itemsPerRow; col++) {
          const slide = document.createElement("div");
          slide.className = "embla__slide embla__slide-group";
          slide.style.setProperty("--slide-rows", rows);
          for (let row = 0; row < rows; row++) {
            const item = page[row * itemsPerRow + col];
            if (item) slide.appendChild(item);
          }
          slides.push(slide);
        }
      }
    }
    const container = this.nodes.container;
    if (container) container.replaceChildren(...slides);
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    var _a, _b, _c, _d, _e, _f, _g;
    this.cleanupFunctions.forEach((fn) => {
      try {
        fn();
      } catch {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch {
      }
      this.emblaApi = null;
    }
    if (this._marqueeActive) {
      this._marqueeStopRAF();
      (_a = this._marqueeIO) == null ? void 0 : _a.disconnect();
      (_b = this._marqueeRO) == null ? void 0 : _b.disconnect();
      if (this._marqueeEnter) {
        (_c = this.nodes.viewport) == null ? void 0 : _c.removeEventListener("pointerenter", this._marqueeEnter);
        (_d = this.nodes.viewport) == null ? void 0 : _d.removeEventListener("pointerleave", this._marqueeLeave);
        this._marqueeEnter = this._marqueeLeave = null;
      }
      if (this._marqueeOnDown) {
        (_e = this.nodes.viewport) == null ? void 0 : _e.removeEventListener("pointerdown", this._marqueeOnDown);
        (_f = this.nodes.viewport) == null ? void 0 : _f.removeEventListener("pointermove", this._marqueeOnMove);
        (_g = this.nodes.viewport) == null ? void 0 : _g.removeEventListener("pointerup", this._marqueeOnUp);
        if (this.nodes.viewport) {
          this.nodes.viewport.style.touchAction = "";
          this.nodes.viewport.style.cursor = "";
        }
        this._marqueeOnDown = this._marqueeOnMove = this._marqueeOnUp = null;
      }
      if (this.nodes.container) {
        this.nodes.container.querySelectorAll("[data-cloned]").forEach((el) => el.remove());
        this.nodes.container.style.animation = "";
        this.nodes.container.style.transform = "";
        this.nodes.container.style.removeProperty("--_marquee-gw");
      }
      this._marqueeActive = false;
      this._marqueeCssIdle = false;
      this._marqueeIO = null;
      this._marqueeRO = null;
    }
  }
}
if (!customElements.get("listing-component")) {
  customElements.define("listing-component", ListingComponent);
}
